/**
 * "Did you mean …?" for the "My camera isn't listed" dialog (C1, ENG-4832).
 *
 * Pure: the connected mount gathers the registry rows, this module decides
 * which listed model the typed text most likely names. A suggestion is offered
 * only for a strong match — every typed field must score at least
 * `MatchScore.wordPrefix` — so a stray substring never pulls a user who
 * really has an unlisted camera back into the list.
 */
import { MatchScore, scoreMatch } from '@studio/design-system/combobox';
import type { Brand, Model } from '@studio/models/device';
import type { Collection } from '#~/api/device/registry/index.js';
import { getNickname } from '#~/device/registry/index.js';
import { isOtherBrand } from '../video/rtsp/utils.js';

/** What the user typed into C1. */
export interface NotListedQuery {
  brand: string;
  model: string;
}

/** A listed model the typed text may refer to. */
export interface NotListedSuggestion {
  brandId: Brand['id'];
  modelId: Model['id'];
  /** "<Brand> <Model>", the same rule as the default device nickname. */
  label: string;
}

/** One brand/model pair of the registry catalogue. */
export interface CatalogueRow {
  brand: Brand;
  model: Model;
}

/**
 * Flatten loaded registry collections into brand/model rows, dropping entries
 * that are never a useful suggestion: the catch-all "Other" brand, each
 * brand's generic "Other" model, and not-yet-supported models (ENG-4832) —
 * those have no driver, so accepting one would send the operator into the
 * "isn't supported yet" path instead of the generic camera they already chose.
 */
export const toCatalogueRows = (
  brands: Collection<Brand> | undefined,
  modelsByBrand: Readonly<Record<string, Collection<Model> | undefined>>
): CatalogueRow[] => {
  const rows: CatalogueRow[] = [];

  for (const brand of Object.values(brands?.items ?? {}) as Brand[]) {
    if (isOtherBrand(brand)) continue;

    for (const model of Object.values(modelsByBrand[brand.id]?.items ?? {}) as Model[]) {
      // ENG-4832: never suggest a not-yet-supported model — it has no driver, so "did you
      // mean" would route the operator to "isn't supported yet" rather than the generic
      // camera they picked. The registry also flags these `generic`, but the explicit check
      // keeps them out even if that invariant ever loosens.
      if (model.generic || model.notYetSupported === true) continue;
      rows.push({ brand, model });
    }
  }

  return rows;
};

/**
 * Brands whose models are worth fetching for the typed brand: only strong
 * matches, capped, so typing never fans out a registry request per brand.
 */
export const matchingBrandIds = (
  typedBrand: string,
  brands: Collection<Brand> | undefined,
  limit = 3
): Brand['id'][] =>
  (Object.values(brands?.items ?? {}) as Brand[])
    .filter((brand) => !isOtherBrand(brand))
    .map((brand) => ({ id: brand.id, score: scoreMatch(typedBrand, brand.name) }))
    .filter(({ score }) => score >= MatchScore.wordPrefix)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ id }) => id);

/**
 * Rank listed models against the typed brand/model; best first.
 *
 * - No model typed: nothing to suggest (a brand alone does not pick a model).
 * - Model typed: it must word-prefix match the model name.
 * - Brand typed too: it must also word-prefix match the brand name.
 * Ties keep catalogue order.
 */
export const buildNotListedSuggestions = (
  query: NotListedQuery,
  rows: readonly CatalogueRow[],
  limit = 3
): NotListedSuggestion[] => {
  if (query.model.trim().length === 0) {
    return [];
  }

  const hasBrand = query.brand.trim().length > 0;

  return rows
    .map((row, index) => {
      const label = getNickname(row.brand, row.model);
      // Model text is matched against the model name only: matching "<Brand> <Model>"
      // would let any query that prefixes the brand score as a whole-name hit.
      const modelScore = scoreMatch(query.model, row.model.name);
      const brandScore = hasBrand ? scoreMatch(query.brand, row.brand.name) : MatchScore.exact;

      return { index, label, row, brandScore, modelScore };
    })
    .filter(
      ({ brandScore, modelScore }) =>
        modelScore >= MatchScore.wordPrefix && brandScore >= MatchScore.wordPrefix
    )
    .sort(
      (a, b) => b.brandScore + b.modelScore - (a.brandScore + a.modelScore) || a.index - b.index
    )
    .slice(0, limit)
    .map(({ label, row }) => ({ brandId: row.brand.id, modelId: row.model.id, label }));
};
